// CARO. Casa de estilo — COLORIMETRY ME! P2: the slug of each result page per language
// (/estudio-de-estilo/morfologia/silueta-en-a, /en/style-studio/body-shape/a-shape,
// /fr/studio-de-style/forme-du-visage/visage-coeur…). The Spanish slug is the logical one (code, tests); the
// segments themselves translate in lib/seo/routes.ts. Pure and small on purpose: the URL table imports
// it into every bundle (no JSON here).

import type { Locale } from '@/types';
import { FACE_PATH, SILHOUETTE_PATH } from './route';

type Slugs = Record<Locale, string>;

/** Silhouettes by id (morphology.json): «silueta en A» is how people search it in each language. */
export const SHAPE_SLUGS: Readonly<Record<string, Slugs>> = {
  X: { es: 'silueta-en-x', en: 'x-shape', fr: 'silhouette-en-x' },
  A: { es: 'silueta-en-a', en: 'a-shape', fr: 'silhouette-en-a' },
  V: { es: 'silueta-en-v', en: 'v-shape', fr: 'silhouette-en-v' },
  H: { es: 'silueta-en-h', en: 'h-shape', fr: 'silhouette-en-h' },
  O: { es: 'silueta-en-o', en: 'o-shape', fr: 'silhouette-en-o' },
  I: { es: 'silueta-en-i', en: 'i-shape', fr: 'silhouette-en-i' },
};

/** Faces by id (faces.json). Spanish says «cara» (owner, 2026-10-23: what people search). */
export const FACE_SLUGS: Readonly<Record<string, Slugs>> = {
  oval: { es: 'cara-ovalada', en: 'oval-face', fr: 'visage-ovale' },
  round: { es: 'cara-redonda', en: 'round-face', fr: 'visage-rond' },
  square: { es: 'cara-cuadrada', en: 'square-face', fr: 'visage-carre' },
  oblong: { es: 'cara-rectangular', en: 'oblong-face', fr: 'visage-rectangulaire' },
  diamond: { es: 'cara-diamante', en: 'diamond-face', fr: 'visage-losange' },
  heart: { es: 'cara-corazon', en: 'heart-face', fr: 'visage-coeur' },
  triangle: { es: 'cara-triangular', en: 'triangle-face', fr: 'visage-triangle' },
};

/** Spanish face slugs used before 2026-10-23 («rostro-…»): still accepted, so old links get their 301. */
const LEGACY_FACE_SLUGS: Readonly<Record<string, string>> = {
  'rostro-ovalado': 'cara-ovalada',
  'rostro-redondo': 'cara-redonda',
  'rostro-cuadrado': 'cara-cuadrada',
  'rostro-rectangular': 'cara-rectangular',
  'rostro-rombo': 'cara-diamante',
  'rostro-corazon': 'cara-corazon',
  'rostro-base-ancha': 'cara-triangular',
};

export type StyleGame = 'silhouette' | 'face';

const TABLE: Record<StyleGame, Readonly<Record<string, Slugs>>> = { silhouette: SHAPE_SLUGS, face: FACE_SLUGS };
export const GAME_PATH: Record<StyleGame, string> = { silhouette: SILHOUETTE_PATH, face: FACE_PATH };

/** The logical path of a result page (add the language with localizedPath). */
export function stylePath(game: StyleGame, id: string): string {
  const slugs = TABLE[game][id];
  if (!slugs) throw new Error(`styleSlugs: ${game} desconocido ${id}`);
  return `${GAME_PATH[game]}/${slugs.es}`;
}

/** The id behind a logical (Spanish) slug, or undefined. */
export function styleIdBySlug(game: StyleGame, esSlug: string): string | undefined {
  return Object.entries(TABLE[game]).find(([, s]) => s.es === esSlug)?.[0];
}

/** Public slug in `locale` of a Spanish slug (unknown: unchanged). */
export const styleSlugFor = (game: StyleGame) => (locale: Locale, esSlug: string): string =>
  Object.values(TABLE[game]).find((s) => s.es === esSlug)?.[locale] ?? esSlug;

/** Spanish slug of a public slug: the locale's own wins, any language's is accepted (old or mixed URLs). */
export const styleEsSlug = (game: StyleGame) => (locale: Locale, slug: string): string => {
  const all = Object.values(TABLE[game]);
  const found = (all.find((s) => s[locale] === slug) ?? all.find((s) => s.es === slug || s.en === slug || s.fr === slug))?.es;
  return found ?? (game === 'face' ? LEGACY_FACE_SLUGS[slug] : undefined) ?? slug;
};
